import { Controller, useFormContext } from 'react-hook-form';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useUserRoles, useEngagementPermissions } from '@anytag/auth';
import { InfluencerAvatar, SpreadButton } from '@anytag/shared-molecules';
import { formatIntNumber, formatPercent, formatNumber } from '@anytag/shared-utils';
import { useCopy, useGenerateInfluencerProfileLink, useQueryHelper, useTableInputCursor } from '@anytag/shared-hooks';
import {
  TTCMStatus,
  EngagementDetail,
  EngagementCampaignStatus,
  EngagementProposalSocialMediaType,
  ProposalJobDescriptionBreakDownMode,
  useDeleteEngagementProposalJoinedAccountMutation
} from '@anytag/graphql';
import {
  Anchor,
  Button,
  CheckBox,
  TTCMBadge,
  TableCell,
  SocialIcon,
  CheckBoxProps,
  AnchorWithStatus
} from '@anytag/shared-atoms';
import { EngagementProposalRowData, EngagementProposalInfluencersFormValues, EditableFieldKeys } from '../types';
import { useEngagementProposalInfluencerDetailsParam } from './useEngagementProposalInfluencerDetailsParam';

interface Props {
  isEditMode?: boolean;
  hasRowChange?: boolean;
  listSelectedIds?: number[];
  engagement: EngagementDetail;
  hasTrackingReferral?: boolean;
  selectButtonProps: CheckBoxProps;
  hasInfluencerCostPermission?: boolean;
  jdMode: ProposalJobDescriptionBreakDownMode;
  onRowSelect: (influencerId: number, socialAccountId?: number) => void;
}

export const useEngagementProposalTable = ({
  jdMode,
  isEditMode,
  onRowSelect,
  selectButtonProps,
  hasTrackingReferral,
  listSelectedIds = [],
  hasInfluencerCostPermission,
  engagement: { id, status, currency, isTtcmCampaign, adAgencyMarginRate }
}: Props) => {
  const { copyToClipboard } = useCopy(null);
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const { t, enqueueSnackbar, trimDotsFromMessage, invalidateRouteLoader } = useQueryHelper();
  const { control, watch, setValue } = useFormContext<EngagementProposalInfluencersFormValues>();
  const { isAdvertiser, isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { handleInputBlur, handleInputFocus } = useTableInputCursor();
  const { hideToggleInfluencerFromProposalBtn } = useEngagementPermissions();
  const { setInfluencerDetailsParam } = useEngagementProposalInfluencerDetailsParam();
  const { callDeleteEngagementProposalJoinedAccount, loading: removing } =
    useDeleteEngagementProposalJoinedAccountMutation({ onCompleted: invalidateRouteLoader });

  const values = watch();

  const hasNoAgencyRateOrAdvertiser = !adAgencyMarginRate || isAdvertiser;
  const isDraftOrReviewingOrOngoingOrUpcomingStatus = [
    EngagementCampaignStatus.DRAFT,
    EngagementCampaignStatus.REVIEWING,
    EngagementCampaignStatus.ONGOING,
    EngagementCampaignStatus.UPCOMING
  ].includes(status);

  const handleRemoveSmAccountFromProposal = async ({
    influencerId,
    socialAccountId,
    socialAccountMedia
  }: {
    influencerId: number;
    socialAccountId: number;
    socialAccountMedia: EngagementProposalSocialMediaType;
  }) => {
    try {
      await callDeleteEngagementProposalJoinedAccount({
        variables: { input: { campaignId: id, influencerId, socialAccountId, socialAccountMedia } }
      });
      // we don't want to notify user on each save/delete, just show an error if happens
    } catch (error) {
      enqueueSnackbar(t(trimDotsFromMessage(error.message)), { variant: 'error' });
    }
  };

  const handleInputChange =
    (row: Row<EngagementProposalRowData>, key: EditableFieldKeys) =>
    ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
      if (isEditMode) {
        // Make number always positive
        const inputValue = Math.abs(value ? Number(value) : 0);
        setValue(name as keyof EngagementProposalInfluencersFormValues, value, { shouldDirty: true });

        switch (key) {
          case EditableFieldKeys.netBudget:
            setValue(`${row.index}-budget`, (inputValue * (1 + Number(adAgencyMarginRate || 0))).toString(), {
              shouldDirty: true
            });
            setValue(
              `${row.index}-profit`,
              (inputValue - Number(values[`${row.index}-influencerCost`] ?? 0)).toString(),
              { shouldDirty: true }
            );
            break;
          case EditableFieldKeys.influencerCost:
            setValue(`${row.index}-profit`, (Number(values[`${row.index}-netBudget`] ?? 0) - inputValue).toString(), {
              shouldDirty: true
            });
            break;
          default:
            break;
        }
      }
    };

  const columns: ColumnDef<EngagementProposalRowData>[] = [
    {
      size: 40,
      accessorKey: 'select',
      meta: { sticky: true },
      header: () => <CheckBox className="w-4" help={t('Select all')} {...selectButtonProps} />,
      cell: ({ row }) => {
        const { socialAccount, influencer } = row.original;

        const isInfluencerSelected = listSelectedIds.includes(influencer.id);

        if (socialAccount) {
          const isAccountSelected = listSelectedIds.includes(socialAccount.id);

          return isEditMode ? (
            <CheckBox
              checked={isAccountSelected}
              onClick={() => onRowSelect(influencer.id, socialAccount.id)}
              help={isAccountSelected ? t('Unselect') : t('Select')}
            />
          ) : (
            <div className="box-border h-auto flex-none" />
          );
        }

        return !isEditMode || jdMode === ProposalJobDescriptionBreakDownMode.INFLUENCER_BREAKDOWN ? (
          <CheckBox
            checked={isInfluencerSelected}
            onClick={() => onRowSelect(influencer.id)}
            help={isInfluencerSelected ? t('Unselect') : t('Select')}
          />
        ) : (
          <div className="box-border h-auto flex-none" />
        );
      }
    },
    {
      accessorKey: 'influencer',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Influencers'),
      cell: ({ row }) => {
        const { influencer, socialAccount } = row.original;
        const isTTCMCreator = influencer?.ttcmStatus && influencer?.ttcmStatus === TTCMStatus.APPROVED;
        const hasRemoveButton = !isEditMode && !hideToggleInfluencerFromProposalBtn && isAdminStaffTalentAgencyPartner;
        const isShownRemoveButton =
          isTtcmCampaign && influencer.hasOnlyOneTiktokAccount
            ? !!socialAccount?.socialMedia &&
              socialAccount.socialMedia !== EngagementProposalSocialMediaType.TIKTOK &&
              hasRemoveButton
            : hasRemoveButton;

        return socialAccount ? (
          <div className="account-cell">
            <SocialIcon size="24px" type={socialAccount.socialMedia} />
            <Anchor
              label={socialAccount.name}
              className="ml-4 text-sm"
              {...generateProfileLink(influencer.id, {
                filter: {
                  id: String(socialAccount.id),
                  sm:
                    socialAccount.socialMedia === EngagementProposalSocialMediaType.INSTAGRAM_STORY
                      ? EngagementProposalSocialMediaType.INSTAGRAM
                      : socialAccount.socialMedia
                }
              })}
            />
            {isShownRemoveButton ? (
              <SpreadButton
                className="ml-auto [&_img]:size-auto"
                options={[
                  {
                    icon: 'delete',
                    label: t('Remove'),
                    disabled: !isDraftOrReviewingOrOngoingOrUpcomingStatus || removing,
                    onClick: () =>
                      handleRemoveSmAccountFromProposal({
                        influencerId: influencer.id,
                        socialAccountId: socialAccount.id,
                        socialAccountMedia: socialAccount.socialMedia
                      })
                  }
                ]}
              />
            ) : null}
          </div>
        ) : (
          <div className="influencer-cell">
            <InfluencerAvatar
              size="32px"
              src={influencer.avatar}
              className="cursor-pointer rounded-sm"
              onClick={(e) => {
                e.preventDefault();
                setInfluencerDetailsParam(influencer.id);
              }}
            />
            <AnchorWithStatus
              label={influencer.name}
              status={influencer.status}
              className="ml-4 text-lg"
              {...generateProfileLink(influencer.id)}
            />
            {isTTCMCreator ? <TTCMBadge>{t('TTCM')}</TTCMBadge> : null}
          </div>
        );
      }
    },
    {
      accessorKey: 'followers',
      header: t<string>('HeaderColumn.Followers'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats.followersCount)} />
    },
    {
      accessorKey: 'engagementRateByViews',
      header: t<string>('HeaderColumn.Avg ER by views'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.engagementRateByViews, false)} unit="%" />
      )
    },
    {
      accessorKey: 'engagementRateByFollowers',
      header: t<string>('HeaderColumn.Avg ER by followers'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.stats.engagementRateByFollowers, false)} unit="%" />
      )
    },
    {
      accessorKey: 'budget',
      header: t<string>(`HeaderColumn.${hasNoAgencyRateOrAdvertiser ? 'Budget' : 'Gross Budget'}`),
      meta: { align: 'right' },
      cell: ({ row }) =>
        !hasNoAgencyRateOrAdvertiser || !row.original.isEditable ? (
          <TableCell.Number
            value={formatNumber(Number(values[`${row.index}-budget`] ?? 0), 0)}
            unit={t(currency || '')}
          />
        ) : (
          <Controller
            control={control}
            name={`${row.index}-netBudget` satisfies keyof EngagementProposalInfluencersFormValues}
            render={({ field }) => (
              <TableCell.InputNumber
                {...field}
                min={0}
                placeholder="0"
                rightSection={currency}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onChange={handleInputChange(row, EditableFieldKeys.netBudget)}
              />
            )}
          />
        )
    },
    {
      accessorKey: 'netBudget',
      header: t<string>('HeaderColumn.Net Budget'),
      meta: { align: 'right', hidden: hasNoAgencyRateOrAdvertiser },
      cell: ({ row }) =>
        !hasNoAgencyRateOrAdvertiser && !row.original.isEditable ? (
          <TableCell.Number value={formatNumber(row.original.stats.netBudget, 0)} unit={t(currency || '')} />
        ) : (
          <Controller
            control={control}
            name={`${row.index}-netBudget` satisfies keyof EngagementProposalInfluencersFormValues}
            render={({ field }) => (
              <TableCell.InputNumber
                {...field}
                min={0}
                placeholder="0"
                rightSection={currency}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onChange={handleInputChange(row, EditableFieldKeys.netBudget)}
              />
            )}
          />
        )
    },
    {
      accessorKey: 'influencerCost',
      header: t<string>('HeaderColumn.Influencer Cost'),
      meta: { align: 'right', hidden: !hasInfluencerCostPermission || !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) =>
        !row.original.isEditable ? (
          <TableCell.Number value={formatNumber(row.original.stats.influencerCost, 0)} unit={t(currency || '')} />
        ) : (
          <Controller
            control={control}
            name={`${row.index}-influencerCost` satisfies keyof EngagementProposalInfluencersFormValues}
            render={({ field }) => (
              <TableCell.InputNumber
                {...field}
                min={0}
                placeholder="0"
                rightSection={currency}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onChange={handleInputChange(row, EditableFieldKeys.influencerCost)}
              />
            )}
          />
        )
    },
    {
      accessorKey: 'profit',
      header: t<string>('HeaderColumn.Profit'),
      meta: { align: 'right', hidden: !hasInfluencerCostPermission || !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => (
        <TableCell.Number
          value={formatNumber(Number(values[`${row.index}-profit`] ?? 0), 0)}
          unit={t(currency || '')}
        />
      )
    },
    {
      accessorKey: 'addedDate',
      header: t<string>('HeaderColumn.List Added Date'),
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.stats.addedDate} />
    },
    {
      accessorKey: 'engagementProposed',
      header: t<string>('HeaderColumn.Engagement Proposed'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.stats.engagementProposed, 0)} />
    },
    {
      accessorKey: 'engagementPosted',
      header: t<string>('HeaderColumn.Engagement Posted'),
      meta: { align: 'right', hidden: !isAdminStaffTalentAgencyPartner },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.stats.engagementPosted, 0)} />
    },
    {
      accessorKey: 'ttcmCampaignInviteLink',
      header: t<string>('HeaderColumn.Invitation Link'),
      meta: { hidden: !isTtcmCampaign },
      cell: ({ row }) =>
        row.original.stats.ttcmCampaignInviteLink ? (
          <Button
            variant="outline"
            className="px-6"
            title={t('Button.Copy Link')}
            onClick={() => copyToClipboard(row.original.stats.ttcmCampaignInviteLink)}
          />
        ) : (
          <div />
        )
    },
    {
      accessorKey: 'couponCode',
      header: t<string>('HeaderColumn.Referral Code'),
      meta: { hidden: !hasTrackingReferral },
      cell: ({ row }) => <TableCell.Text className="min-w-12.5" value={row.original.stats.couponCode || ''} />
    }
  ];

  return { columns };
};
